import CloseIcon from "@mui/icons-material/Close";
import { Dialog, LinearProgress } from "@mui/material";
import { styled } from "@mui/system";
import { capitalize } from "lodash";
import React from "react";
import { useDispatch } from "react-redux";
import { formatPrice } from "src/components/portfolio/helpers";
import MagnifyingGlass from "src/icons/MagnifyingGlass";
import { setBuyInput, setSellInput } from "src/store/features/swap.slice";
import CountryOrCryptoFlag from "../CountryOrCryptoFlag";
import { trackEvent } from "src/Amplitude";

export const Wrapper = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    display: "flex",
    width: "462px",
    height: "396px",
    padding: "16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    borderRadius: "16px",
    background: "#FFF",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "fit-content",
    },
  },
}));

export const HeaderAndSearch = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",
  gap: "16px",
}));

export const Header = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignSelf: "stretch",
  flex: 1,
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  color: "#000",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: " -0.32px",
}));

export const CloseButton = styled("div")(() => ({
  cursor: "pointer",
  fontSize: "36px",
}));

export const SearchBarWrapper = styled("div")(() => ({
  position: "relative",
  width: "100%",
}));

export const SearchBar = styled("input")(() => ({
  width: "100%",
  height: "40px",
  display: "flex",
  padding: "12px",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "16px",
  border: "1px solid #E5E7E8",
  outline: "none",

  "&::placeholder": {
    color: "#BDC2C5",
    userSelect: "none",
  },

  "&:hover": {
    border: "1px solid #002035",
  },

  "&:focus": {
    border: "1px solid #002035",
    background: "#FFF",
  },
}));

export const SearchBarIcon = styled("span")(() => ({
  position: "absolute",
  right: "15px",
  top: "30%",
  pointerEvents: "none" /* Makes the icon unclickable */,
}));

export const LoadingWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  alignSelf: "stretch",
}));

export const LoadingWrapperText = styled("span")(() => ({
  alignSelf: "center",
}));

export const ErrorWrapperText = styled("span")(() => ({
  color: "#FC4C4C",
  alignSelf: "center",
}));

export const CoinSelectionList = styled("ul")(() => ({
  all: "unset",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  alignSelf: "stretch",
  overflow: "auto",

  // scroll bar styling
  paddingRight: "10px", // give padding to scrollbar
  /* Style scrollbar for WebKit (Chrome, Safari) */
  "&::-webkit-scrollbar": {
    width: "5px" /* Width of the scrollbar */,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
    borderRadius: "5px" /* Border radius of the scrollbar thumb */,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent" /* Color of the scrollbar thumb */,
  },
}));

export const ListItem = styled("li")(() => ({
  display: "flex",
  padding: "12px",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  background: "#FFF",
  cursor: "pointer",

  "&:hover": {
    background: "#F8F9F9",
  },
}));

export const ItemIcon = styled("div")(() => ({
  display: "flex",
  width: "32px",
  height: "32px",
  justifyContent: "center",
  alignItems: "center",
}));

export const ItemNames = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "2px",
  flex: "1 0 0",
}));

interface ItemTextProps {
  weight?: number;
}
export const ItemText = styled("div")<ItemTextProps>(({ weight }) => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: weight || 400,
  lineHeight: "16px",
}));

export const ItemValues = styled("div")(() => ({
  width: "430px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-end",
  gap: "2px",
  flex: "1 0 0",
}));

export interface ICurrency {
  userBaseCurrency: string;
  code: string;
  displayName: string;
  logoURL: string;
  units: string;
  value: string;
  rank: string;
  sector: string;
  price: number;
}
interface CryptoSelectionModalV2Props {
  open: boolean;
  currencies: ICurrency[];
  isLoading: boolean;
  action: string;
  hasError?: boolean;
  onClose: () => void;
}

const CryptoSelectionModalV2 = (props: CryptoSelectionModalV2Props) => {
  const { open, currencies, isLoading, action, hasError, onClose } = props;
  const dispatch = useDispatch();

  const [currencyList, setCurrencyList] = React.useState<ICurrency[]>([]);
  React.useEffect(() => {
    setCurrencyList(currencies);
  }, [currencies]);

  const handleCurrencyClick = (currency: ICurrency): void => {
    const { code, logoURL, units, price } = currency;
    if (action === "sell") dispatch(setSellInput({ code, logoURL, maxTotal: units, price }));
    if (action === "buy") dispatch(setBuyInput({ code, logoURL, maxTotal: units, price }));
    handleCloseModal();
  };

  const getLoadingText = (): string | undefined => {
    if (action === "sell") return "Grabbing your holdings...";
    if (action === "buy") return "Grabbing our currencies...";
  };

  const getPrimaryByAction = (currency: ICurrency): string | undefined => {
    if (action === "sell") return `${currency.units} ${currency.code}`;
    if (action === "buy") return `${currency.rank}`;
  };

  const getSecondaryByAction = (currency: ICurrency): string | undefined => {
    if (action === "sell") return formatPrice(Number(currency.value), currency.userBaseCurrency);
    if (action === "buy") return `${currency.sector}`;
  };

  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchCurrencies = (): void => {
    if (!currencies) return;
    const filteredCurrencies = currencies.filter((currency) => {
      return (
        currency.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        currency.code.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
    setCurrencyList(filteredCurrencies);
  };

  React.useEffect(() => {
    handleSearchCurrencies();
  }, [searchQuery]);

  const handleCloseModal = (): void => {
    setSearchQuery("");
    onClose();
  };

  React.useEffect(() => {
    if (open && action === "sell") {
      trackEvent("Visit_Modal", { modal: "Sell_Crypto_Selection" }, "CryptoSelectionModalV2");
    }
    if (open && action === "buy") {
      trackEvent("Visit_Modal", { modal: "Buy_Crypto_Selection" }, "CryptoSelectionModalV2");
    }
  }, [open, action]);

  return (
    <Wrapper open={open} onClose={handleCloseModal}>
      <HeaderAndSearch>
        {/* header */}
        <Header>
          Select Currency to {capitalize(action)}
          {/* close button */}
          <CloseButton onClick={onClose}>
            <CloseIcon fontSize="inherit" />
          </CloseButton>
        </Header>

        {/* searchbar */}
        <SearchBarWrapper>
          <SearchBar
            autoFocus
            placeholder="Search"
            onChange={handleSearchChange}
            value={searchQuery}
          />
          <SearchBarIcon>
            <MagnifyingGlass />
          </SearchBarIcon>
        </SearchBarWrapper>
      </HeaderAndSearch>

      {isLoading && (
        <LoadingWrapper>
          <LoadingWrapperText>{getLoadingText()}</LoadingWrapperText>
          <LinearProgress />
        </LoadingWrapper>
      )}

      {currencyList.length <= 0 && hasError && (
        <ErrorWrapperText>Something went wrong. Please try again later.</ErrorWrapperText>
      )}

      {currencyList.length <= 0 && !hasError && !isLoading && (
        <ErrorWrapperText>No holdings to trade with.</ErrorWrapperText>
      )}

      {/* coin selection list */}
      <CoinSelectionList id="crypto-selection-list">
        {currencyList &&
          currencyList.map((currency) => (
            <ListItem
              id={`listItem-${action}-${currency.code}`}
              key={currency.code}
              onClick={() => handleCurrencyClick(currency)}>
              <ItemIcon>
                <CountryOrCryptoFlag name={currency.logoURL || currency.code} size={32} />
              </ItemIcon>
              <ItemNames>
                <ItemText weight={500}>{currency.code}</ItemText>
                <ItemText>{currency.displayName}</ItemText>
              </ItemNames>
              <ItemValues>
                <ItemText weight={500}>{getPrimaryByAction(currency)}</ItemText>
                <ItemText>{getSecondaryByAction(currency)}</ItemText>
              </ItemValues>
            </ListItem>
          ))}
      </CoinSelectionList>
    </Wrapper>
  );
};

export default CryptoSelectionModalV2;
